import { useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import { TOKEN_2022_PROGRAM_ID } from '@solana/spl-token';
import { Button } from '@/components/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/base/card';
import { Separator } from '@/components/base/separator';
import { useAccount } from '@/state/account';

const TOKEN_2022_BASE58 = TOKEN_2022_PROGRAM_ID.toBase58();

function shortMint(m: string) {
  return `${m.slice(0, 4)}…${m.slice(-4)}`;
}

export function BalanceCard() {
  const { account, network, solBalance, solBalanceError, holdings, holdingsError, refreshBalances, refreshing } =
    useAccount();

  useEffect(() => {
    if (!account) return;
    void refreshBalances();
  }, [account, network, refreshBalances]);

  return (
    <Card>
      <CardHeader className="para:flex-row para:items-center para:justify-between">
        <CardTitle>Balances</CardTitle>
        <Button
          variant="ghost"
          size="icon"
          onClick={refreshBalances}
          disabled={refreshing}
          title="Refresh"
        >
          <RefreshCw className={refreshing ? 'para:size-4 para:animate-spin' : 'para:size-4'} />
        </Button>
      </CardHeader>
      <CardContent className="para:flex para:flex-col para:gap-3">
        <div className="para:flex para:items-center para:justify-between para:py-2">
          <div className="para:flex para:flex-col">
            <span className="para:text-sm para:font-medium">SOL</span>
            <span className="para:text-xs para:text-muted-foreground">Native</span>
          </div>
          <span className="para:font-mono para:text-base">
            {solBalance === null ? (refreshing ? '…' : '—') : solBalance.toFixed(6)}
          </span>
        </div>
        {holdings.length > 0 && <Separator />}
        {holdings.map((h) => (
          <div key={h.mint + h.programId} className="para:flex para:items-center para:justify-between para:py-1">
            <div className="para:flex para:flex-col">
              <span className="para:text-sm para:font-medium">{shortMint(h.mint)}</span>
              <span className="para:text-xs para:text-muted-foreground">
                {h.programId === TOKEN_2022_BASE58 ? 'Token-2022' : 'SPL Token'}
              </span>
            </div>
            <span className="para:font-mono para:text-sm">{h.uiAmount}</span>
          </div>
        ))}
        {solBalanceError && (
          <p className="para:text-sm para:text-destructive">{solBalanceError}</p>
        )}
        {holdingsError && (
          <p className="para:text-sm para:text-muted-foreground">
            Token list unavailable on the configured RPC. Set a Custom RPC in the header (Alchemy, Helius, or
            QuickNode) to populate the SPL token select.
          </p>
        )}
        {!refreshing && solBalance === 0 && holdings.length === 0 && !solBalanceError && !holdingsError && (
          <p className="para:text-sm para:text-muted-foreground">No balances found on this network.</p>
        )}
      </CardContent>
    </Card>
  );
}
